import {
  faEye,
  faEyeSlash,
  type IconDefinition,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  type Dispatch,
  type HTMLInputTypeAttribute,
  type SetStateAction,
  useState,
} from "react"
import { cn } from "cn"
import { InputGroup, InputGroupAddon, InputGroupInput } from "./input-group"

export type InputProps = React.ComponentProps<"input"> & {
  startIcon?: IconDefinition
  endIcon?: IconDefinition
  showPassword?: boolean
}

function PasswordIcon({
  showPassword,
  type,
  isPasswordVisible,
  setIsPasswordVisible,
}: {
  showPassword?: boolean
  type?: HTMLInputTypeAttribute
  isPasswordVisible: boolean
  setIsPasswordVisible: Dispatch<SetStateAction<boolean>>
}) {
  return showPassword && type === "password" ? (
    <FontAwesomeIcon
      className={cn(
        "transform text-neutrals-700 transition-colors group-focus-within:text-primary-blue-600",
        "absolute top-1/2 right-3 -translate-y-1/2"
      )}
      icon={isPasswordVisible ? faEyeSlash : faEye}
      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
    />
  ) : (
    <></>
  )
}

function Input({
  className,
  type,
  startIcon,
  endIcon,
  showPassword,
  ...props
}: InputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  function getInputType() {
    return type === "password"
      ? isPasswordVisible
        ? "text"
        : "password"
      : type
  }

  return (
    <div className={cn("group relative rounded-md bg-transparent", className)}>
      {startIcon && (
        <FontAwesomeIcon
          icon={startIcon}
          className={cn(
            "transform text-neutrals-700 transition-colors group-focus-within:text-primary-blue-600",
            "absolute top-1/2 left-2.5 -translate-y-1/2"
          )}
        />
      )}
      <input
        type={getInputType()}
        className={cn(
          "peer",
          "flex w-full py-2",
          `${startIcon ? "ps-8" : "ps-4"}`,
          `${endIcon ? "pe-9" : "pe-4"}`,
          "rounded-md border border-neutrals-400",
          "bg-white text-sm leading-6 transition-colors",
          "file:text-md file:border-0 file:bg-transparent file:font-medium",
          "focus-visible:border-neutrals-1000 focus-visible:outline-hidden",
          "disabled:cursor-not-allowed disabled:opacity-70",
          "placeholder:text-neutrals-700",
          className
        )}
        data-slot="input"
        {...props}
      />
      {endIcon && (
        <FontAwesomeIcon
          className={cn(
            "transform text-neutrals-700 transition-colors group-focus-within:text-primary-blue-600",
            "absolute top-1/2 right-3 -translate-y-1/2"
          )}
          icon={endIcon}
        />
      )}
      <PasswordIcon
        isPasswordVisible={isPasswordVisible}
        setIsPasswordVisible={setIsPasswordVisible}
        type={type}
        showPassword={showPassword}
      />
    </div>
  )
}

function DatePickerInput({
  startIcon,
  endIcon,
  className,
  ...props
}: InputProps) {
  return (
    <InputGroup className={className}>
      {startIcon && (
        <InputGroupAddon align="inline-start">
          <FontAwesomeIcon
            icon={startIcon}
            className="transform text-neutrals-700 transition-colors group-focus-within:text-primary-blue-600"
          />
        </InputGroupAddon>
      )}
      <InputGroupInput {...props} className={className} />
      {endIcon && (
        <InputGroupAddon align="inline-end">
          <FontAwesomeIcon
            icon={endIcon}
            className="transform text-neutrals-700 transition-colors group-focus-within:text-primary-blue-600"
          />
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}

export { Input, DatePickerInput }
